"use client";

import { useEffect, useState, type MouseEvent } from "react";
import type { LegalSection } from "@/components/legal/types";

function handleJump(e: MouseEvent<HTMLAnchorElement>, id: string) {
  e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  window.history.replaceState(null, "", `#${id}`);
  const details = (e.currentTarget as HTMLElement).closest("details");
  if (details) details.removeAttribute("open");
}

export function LegalToc({ sections }: { sections: LegalSection[] }) {
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const ids = sections.map((s) => s.id);
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        for (const id of ids) {
          if (visible.has(id)) {
            setActiveId(id);
            break;
          }
        }
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  const list = (
    <ol className="space-y-0.5">
      {sections.map((s) => {
        const active = activeId === s.id;
        return (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              onClick={(e) => handleJump(e, s.id)}
              aria-current={active ? "true" : undefined}
              className={`block border-l-2 py-1 pl-3 text-[13px] leading-snug transition-colors ${
                active
                  ? "border-brand-electric font-medium text-brand-electric"
                  : "border-transparent text-on-surface-variant hover:border-outline-variant hover:text-brand-navy"
              }`}
            >
              {s.title}
            </a>
          </li>
        );
      })}
    </ol>
  );

  return (
    <>
      <details className="mb-8 rounded-lg border border-outline-variant/60 bg-[#f7f9fc] lg:hidden">
        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-brand-navy">
          Contents
        </summary>
        <div className="max-h-[60vh] overflow-y-auto border-t border-outline-variant/60 px-2 py-2">
          {list}
        </div>
      </details>

      <nav
        aria-label="Contents"
        className="sticky top-24 hidden max-h-[calc(100vh-7rem)] overflow-y-auto pr-2 lg:block"
      >
        <p className="mb-2 pl-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant">
          Contents
        </p>
        {list}
      </nav>
    </>
  );
}
